import {
  useRef,
  useState,
  useCallback,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
  type CSSProperties,
} from 'react'
import { useNls } from '../../nls/nlsProvider'
import { MiniLoader } from './MiniLoader'
import { Pressable } from './Pressable'

export interface PullToRefreshProps {
  onRefresh: () => Promise<unknown> | void
  threshold?: number
  className?: string
  children: ReactNode
}

const DEFAULT_THRESHOLD = 64

export const PullToRefresh = ({
  onRefresh,
  threshold = DEFAULT_THRESHOLD,
  className,
  children,
}: PullToRefreshProps) => {
  const { t } = useNls()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const startYRef = useRef<number | null>(null)
  const pendingRef = useRef(false)
  const [pullDistance, setPullDistance] = useState(0)
  const [isPending, setIsPending] = useState(false)

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const triggerRefresh = useCallback(async () => {
    if (pendingRef.current) return
    pendingRef.current = true
    setIsPending(true)
    setPullDistance(0)
    try {
      await onRefresh()
    } finally {
      pendingRef.current = false
      setIsPending(false)
    }
  }, [onRefresh])

  const isAtScrollTop = () => {
    let el: HTMLElement | null = containerRef.current
    while (el) {
      if (el.scrollTop > 0) return false
      el = el.parentElement
    }
    return true
  }

  const handleTouchStart = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (pendingRef.current) return
    if (!isAtScrollTop()) {
      startYRef.current = null
      return
    }
    const touch = event.touches[0]
    if (!touch) return
    startYRef.current = touch.clientY
  }

  const handleTouchMove = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (pendingRef.current || startYRef.current === null) return
    const touch = event.touches[0]
    if (!touch) return
    const delta = touch.clientY - startYRef.current
    if (delta > 0 && isAtScrollTop()) {
      const damped = prefersReducedMotion ? delta : Math.min(threshold * 1.6, delta * 0.65)
      setPullDistance(delta >= threshold ? Math.max(threshold, damped) : damped)
    } else {
      setPullDistance(0)
    }
  }

  const handleTouchEnd = () => {
    if (pendingRef.current || startYRef.current === null) {
      startYRef.current = null
      return
    }
    const distance = pullDistance
    startYRef.current = null
    if (distance >= threshold) {
      void triggerRefresh()
    } else {
      setPullDistance(0)
    }
  }

  const srOnlyFocusStyle: CSSProperties = {
    position: 'absolute',
    top: '4px',
    right: '8px',
    zIndex: 25,
  }

  const contentTransformStyle: CSSProperties =
    !prefersReducedMotion && pullDistance > 0 && !isPending
      ? {
          transform: `translateY(${Math.min(threshold, Math.round(pullDistance * 0.5))}px)`,
          transition: 'none',
        }
      : {
          transition: prefersReducedMotion ? 'none' : 'transform 180ms ease',
        }

  return (
    <div
      ref={containerRef}
      className={`pullToRefresh relative ${className ?? ''}`.trim()}
      data-testid="pull-to-refresh"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <Pressable
        type="button"
        className="sr-only focus:not-sr-only px-3 py-2 text-xs font-semibold rounded-[var(--radius-ctl)] bg-[var(--surface-raised)] text-[var(--text-strong)] border border-[var(--border)]"
        style={srOnlyFocusStyle}
        disabled={isPending}
        onClick={() => void triggerRefresh()}
      >
        {t('NLSOverviewPhoneRefresh')}
      </Pressable>

      {(isPending || pullDistance > 8) && (
        <div
          className="pullToRefresh__indicator flex items-center justify-center py-2"
          data-testid="pull-to-refresh-indicator"
          aria-live="polite"
        >
          {isPending ? (
            <MiniLoader size="sm" label={t('NLSOverviewPhoneRefreshing')} />
          ) : (
            <span className="text-xs font-semibold text-[var(--muted)]">
              {t('NLSOverviewPhoneRefresh')}
            </span>
          )}
        </div>
      )}

      <div style={contentTransformStyle}>{children}</div>
    </div>
  )
}
export default PullToRefresh
